import { Directive, AfterViewInit, OnInit, OnDestroy, ElementRef, HostBinding, Renderer2, HostListener } from '@angular/core';

import { DrawCollapseService } from './draw-collapse.shared.service';
import { DevService } from '@shared/core/services';

import { Subscription, fromEvent } from 'rxjs';
import { filter, tap, withLatestFrom, pairwise, map, delay } from 'rxjs/operators';

@Directive({
    selector: '[directive-draw-collapse]',
})
export class DrawCollapseSharedDirective implements OnInit, AfterViewInit, OnDestroy {
    private readonly _speedFactor: number = 1;
    private _expandListener$: Subscription;
    private _offsetListener$: Subscription;
    private _offsetMobileListener$: Subscription;
    private _scrollListener$: Subscription;
    private _navBarHeightListener$: Subscription;
    private _headerBaseHeightListener$: Subscription;

    private _isCollapsed: boolean = false;
    private _init: boolean = false;
    private _elem: HTMLElement;
    private _expand: boolean;
    private _mobileScrollPoint: number;
    private _mobileScrollStop: number = 60;

    private _topOffset: number = 0;
    // private _minimalHeight: number;
    // private _maximumHeight: number;
    private _isMobileCollapsed: boolean = false;

    public expandedHeight: number; // = this._minimalHeight;

    constructor(
        private _drawCollapseService: DrawCollapseService,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _d: DevService,
    ) {

    }

    ngOnInit(): void {
        this._elem = this._elementRef.nativeElement;

        this._mobileScrollPoint = this._drawCollapseService.mobileScrollPoint;


        this._headerBaseHeightListener$ = this._drawCollapseService.drawCanvasHeightChange$
            .subscribe(() => {
                this.expandedHeight = this._calcMaximumExpandedHeight();
            });

        this._navBarHeightListener$ = this._drawCollapseService.drawNavBarCollapseHeight$
            .subscribe(height => {
                this._renderer2.setStyle(document.body, 'padding-top', height + 'px');
                this._renderer2.setStyle(this._elem, 'height', `${height}px`);
            });

        this._offsetMobileListener$ = this._drawCollapseService.drawMobileOffset$
            .subscribe(offset => this._mobileScrollStop = offset);

        this._offsetListener$ = this._drawCollapseService.drawOffset$
            .subscribe(offset => this._topOffset = offset);

        this._expandListener$ = this._drawCollapseService.drawExpand$
            .pipe(
                filter(expand => expand !== null),
            )
            .subscribe(expand => {
                this._expand = expand;

                this.expandedHeight = this._calcMaximumExpandedHeight();

                if (expand) {
                    this._expandStart();
                } else {
                    this._collapseStart();
                }
            });

        //
        //  TODO
        //  Regular scroll function works different on different platforms.
        //  This solution only works for DESKTOP CHROME and DESKTOP FIREFOX.
        //  Need to create special scroll adapter, that will trigger additional event
        //  during scrolling on each requestAnimationFrame with current scroll position (scrollY || pageYOffset).
        //
        //  This is also worth reading https://stackoverflow.com/questions/5527601/normalizing-mousewheel-speed-across-browsers
        //  but due to some changes in last 4 years, normalizing scroll is not possible any more in the browsers.
        //
        this._scrollListener$ = fromEvent(window, 'scroll')
            .pipe(
                filter(() => this._expand === true),
                map(event => Object.hasOwnProperty('scrollY') ? window.scrollY : window.pageYOffset),
                pairwise(),
            )
            .subscribe(([prevScrollY, nextScrollY]) => {
                if (prevScrollY < 0 || nextScrollY < 0) return; /* ISO FIX - bouncing back stuff TOLO-185 */

                const delta: number = prevScrollY > nextScrollY ? -1 : 1;
                const diff: number = nextScrollY - prevScrollY;
                const expandedHeight = this._calcMaximumExpandedHeight();
                const collapsedHeight = this._topOffset;
                const elemHeight = this._elem.clientHeight;
                const bodyTopPadding: number = window.innerWidth < this._drawCollapseService.mediaBreakPoint ?
                    parseInt(window.getComputedStyle(document.body).getPropertyValue('padding-top'), 10) : 0;

                let newHeight = Math.ceil((elemHeight - (diff * this._speedFactor)));

                // this._d.log(`prevScroll ${prevScrollY}, nextScrollY ${nextScrollY}`)
                if (delta > 0) {
                    /* Scrolling down / next */
                    /* Prevent from strange jumping when scrolled down */
                    const totalHeight: number = document.body.clientHeight;
                    const bottomEdge: number = window.innerHeight + nextScrollY;
                    if (totalHeight <= bottomEdge) return;

                    const canCollapseMore: boolean = newHeight > collapsedHeight;
                    if (canCollapseMore) {
                        return this._updateHeightValues(newHeight);
                    }

                    if (!this._isCollapsed) {
                        /* Final pull to collapsed height - making sure element height is equal to collapsed height */
                        return this._updateHeightValues(collapsedHeight);
                    }

                    if (this._isCollapsed) {
                        /* Collapsing navBar */
                        const canHideNavBarOnMobile: boolean = canCollapseMore === false
                            && nextScrollY > newHeight + this._mobileScrollPoint
                            && bodyTopPadding !== this._mobileScrollStop && bodyTopPadding > this._mobileScrollStop
                            && window.innerWidth < this._drawCollapseService.mediaBreakPoint;

                        if (canHideNavBarOnMobile) {
                            const newOffset = bodyTopPadding - diff;
                            this._isMobileCollapsed = true;
                            if (newOffset < this._mobileScrollStop) {
                                this._drawCollapseService.setNavBarHeight(this._mobileScrollStop);
                            } else {
                                this._drawCollapseService.setNavBarHeight(newOffset);
                            }
                        }
                    }


                } else {
                    /* Scrolling up / back / prev */
                    const edgeHeight = expandedHeight - collapsedHeight;
                    const canExpand: boolean = nextScrollY <= edgeHeight;
                    if (this._isCollapsed) {
                        /* Expanding nav bar */
                        const canExpandNavBar: boolean = canExpand === false
                            && nextScrollY < edgeHeight + this._mobileScrollPoint
                            && bodyTopPadding < collapsedHeight
                            && window.innerWidth < this._drawCollapseService.mediaBreakPoint;

                        const fullExpand = bodyTopPadding + (prevScrollY - nextScrollY) < collapsedHeight;

                        if (this._isMobileCollapsed) {
                            /* Start expanding mobile collapsed elem stright away */
                            if (bodyTopPadding < collapsedHeight) {
                                this._drawCollapseService.setNavBarHeight(bodyTopPadding + (prevScrollY - nextScrollY));
                            } else {
                                this._isMobileCollapsed = false;
                            }
                        }

                        if (canExpandNavBar && fullExpand) {
                            this._drawCollapseService.setNavBarHeight(bodyTopPadding + (prevScrollY - nextScrollY));
                        }
                    }

                    if (canExpand) {
                        if (newHeight <= expandedHeight) {
                            return this._updateHeightValues(newHeight);
                        }

                        return this._updateHeightValues(expandedHeight);
                    }
                }
            });

        this._renderer2.setStyle(this._elem, 'transition', `height ${this._drawCollapseService.transition}`);
        this._renderer2.setStyle(document.body, 'transition', `padding-top ${this._drawCollapseService.transition}`);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this._drawCollapseService.initDrawDirective();
        }, 500);
    }

    ngOnDestroy(): void {
        if (this._expandListener$ && !this._expandListener$.closed) this._expandListener$.unsubscribe();

        if (this._offsetListener$ && !this._offsetListener$.closed) this._offsetListener$.unsubscribe();

        if (this._offsetMobileListener$ && !this._offsetMobileListener$.closed) this._offsetMobileListener$.unsubscribe();

        if (this._scrollListener$ && !this._scrollListener$.closed) this._scrollListener$.unsubscribe();

        if (this._navBarHeightListener$ && !this._navBarHeightListener$.closed) this._navBarHeightListener$.unsubscribe();

        if(this._headerBaseHeightListener$ && !this._headerBaseHeightListener$.closed) this._headerBaseHeightListener$.unsubscribe();

        this._renderer2.setStyle(this._elem, 'transition', null);
        this._renderer2.setStyle(document.body, 'transition', null);
        this._renderer2.setStyle(this._elem, 'height', null);
        this._renderer2.setStyle(document.body, 'padding-top', null);

        this._drawCollapseService.destroy();
    }

    private _calcMaximumExpandedHeight(): number {
        const bottomPadding = 50;
        this.expandedHeight = this._drawCollapseService.drawCanvasExpandedHeight + this._topOffset + bottomPadding;

        this._drawCollapseService.maxContentMargin = this._drawCollapseService.drawCanvasExpandedHeight + bottomPadding;

        return this.expandedHeight;
    }

    private _expandStart(): void {
        this._setTargetHeight(this._calcMaximumExpandedHeight(), true);
    }

    private _collapseStart(): void {
        this._setTargetHeight(this._topOffset, false);
    }

    private _updateHeightValues(height: number): void {
        const collapsedHeight = this._topOffset;
        height = height > this.expandedHeight ? this.expandedHeight : height;

        if (height <= collapsedHeight) {
            this._isCollapsed = true;
        } else {
            this._isCollapsed = false;
        }

        this._renderer2.setStyle(this._elem, 'height', `${height}px`);
        this._renderer2.setStyle(document.body, 'padding-top', `${height}px`);

        this._drawCollapseService.updateDrawHeight(height);

        const difference = this._drawCollapseService.expand ? this.expandedHeight - height : 0; /* For adding additional margin preventing from initial content scrolling */
        /* Update collapsed percent value for better opacity handling */
        const progress = (((this._elem.clientHeight - this._topOffset) * 100) / (this.expandedHeight - this._topOffset));
        this._drawCollapseService.setProgress(progress / 100, difference);

        window.dispatchEvent(new Event('update')); /* Kind of custom event to inform other components about changes - check max-screen-height-directive */
    }

    private async _setTargetHeight(targetHeight: number, expand: boolean = true, transitionTime: number = this._drawCollapseService.transitionTime): Promise<any> {
        return new Promise(resolve => {

            if (!this._init) {
                this._init = true;

                if (!expand) {
                    this._updateHeightValues(targetHeight);

                    return resolve(true);
                }
            }

            if (this._drawCollapseService.preventOnWindowScrolled) {
                /* Prevent from expanding when user has scrolled down page and it should not be expanded */
                if (window.scrollY >= this._drawCollapseService.drawCanvasExpandedHeight) {
                    return resolve(true);
                }
            }

            let startHeight: number = this._elem.clientHeight;

            if (!expand && startHeight === this._mobileScrollStop) {
                startHeight = targetHeight;
            }

            const startTime: number = 'now' in window.performance ? performance.now() : new Date().getTime();
            const setHeight = () => {
                const now: number = 'now' in window.performance ? performance.now() : new Date().getTime();
                const time: number = Math.min(1, ((now - startTime) / this._drawCollapseService.transitionTime));
                const timeFunction: any = this._easing(time);
                const currentHeightSet = Math.ceil((timeFunction * (targetHeight - startHeight)) + startHeight);
                this._updateHeightValues(currentHeightSet);

                /* Check next step */
                const rangeMin: number = targetHeight;
                const rangeMax: number = targetHeight;
                const currentRange: number = Math.floor(this._elem.clientHeight);
                const shouldFinish: boolean = expand && currentRange >= rangeMax || !expand && currentRange <= rangeMin;
                if (shouldFinish) {
                    resolve(true);

                    return;
                }

                if ('requestAnimationFrame' in window === false) {
                    setTimeout(() => setHeight(), 0);
                } else {
                    window.requestAnimationFrame(() => {
                        setHeight();
                    });
                }
            };

            setHeight();

        });
    }

    private _easing(t: number): number {
        return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
    }
}
